/**
 * Wiring the saved manager pairings into React.
 *
 * The overlay in src/domain/identity.ts is a module singleton rather than component state,
 * because the thing reading it is the records engine — pure functions called from four screens
 * that should not each have to be handed a pairing map. So the loader below is the one place
 * that pushes the store's answer into it, and `usePairingVersion` is how a component says
 * "recompute when it changes".
 */

import { useEffect, useSyncExternalStore } from 'react';
import {
  onPairingsChanged, onRosterChanged, pairKey, pairingVersion, rosterVersion, setAddedManagers,
  setPairings,
} from '../domain/identity.js';
import type { LeagueStore } from '../store/types.js';

const SEASON = 2026;

/** A number that changes whenever a pairing is saved or withdrawn. Use it as a memo dependency. */
export function usePairingVersion(): number {
  return useSyncExternalStore(onPairingsChanged, pairingVersion, pairingVersion);
}

/** Changes whenever a manager is added or amended. Same job, for the roster. */
export function useRosterVersion(): number {
  return useSyncExternalStore(onRosterChanged, rosterVersion, rosterVersion);
}

/**
 * Mounted once, at the app shell.
 *
 * The roster matters even more than the pairings here: `GMS` reads through it, so every picker,
 * every checklist and the sign-in screen change the moment a manager is added. Subscribing at the
 * shell means one repaint covers all of them.
 */
export function useRosterLoader(store: LeagueStore): void {
  useEffect(() => {
    let live = true;
    const load = () => void store.managers().then((rows) => {
      if (live) setAddedManagers(rows);
    });
    load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'managers') load();
    });
    return () => {
      live = false;
      off();
    };
  }, [store]);
}

/** Mounted once, at the app shell, so every screen sees the same history. */
export function usePairingLoader(store: LeagueStore): void {
  useEffect(() => {
    let live = true;
    const load = () => void store.teamPairings().then((rows) => {
      if (!live) return;
      setPairings(Object.fromEntries(rows.map((r) => [pairKey(r.season, r.team), r.gm])));
    });
    load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'teamPairings') load();
    });
    return () => {
      live = false;
      off();
    };
  }, [store]);
}
